/**
 * jersey-bundle-cleanup.js
 *
 * Global cart bundle synchronizer. Loaded on any page where the cart renders.
 *
 * Responsibilities:
 *  1. When a parent jersey (line item with `_bundle_id`) changes quantity → mirror that
 *     quantity to every addon (line item with matching `_bundle_parent`).
 *  2. When a parent jersey is removed (no matching `_bundle_id` in cart) → remove
 *     every orphaned addon that points to the missing parent token.
 *
 * Reacts to: any `cart:update` event dispatched anywhere on the page (works with
 * Horizon's cart-items-component, our jersey-addons module, manual fetches).
 *
 * Uses a re-entrancy lock and stable snapshots so it doesn't loop on its own updates.
 */

(function () {
  if (window.__jerseyBundleCleanupBound) return;
  window.__jerseyBundleCleanupBound = true;

  let syncInProgress = false;
  let pendingRerun = false;

  /** Run a sync pass. Coalesces rapid back-to-back triggers into one. */
  async function syncBundles(reason) {
    if (syncInProgress) {
      pendingRerun = true;
      return;
    }
    syncInProgress = true;

    try {
      const cart = await fetchCart();
      if (!cart) return;

      const updates = computeUpdates(cart);
      if (Object.keys(updates).length === 0) return;

      console.debug('[bundle-cleanup] applying updates', { reason, updates });

      const sections = collectCartSectionIds();

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections }),
      });

      if (!res.ok) {
        console.warn('[bundle-cleanup] /cart/update.js failed', res.status);
        return;
      }
      const parsed = await res.json().catch(() => null);

      // Re-broadcast so cart-items-component re-renders. Mark source so our own
      // listener ignores it on the next tick.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: parsed,
            sourceId: 'jersey-bundle-cleanup',
            data: { source: 'jersey-bundle-cleanup', didError: false },
          },
        })
      );
    } catch (err) {
      console.warn('[bundle-cleanup] sync error', err);
    } finally {
      syncInProgress = false;
      if (pendingRerun) {
        pendingRerun = false;
        // Defer one tick to let the event loop settle.
        setTimeout(() => syncBundles('rerun'), 50);
      }
    }
  }

  /**
   * For each parent token in the cart, compute the desired addon quantity.
   *   - If parent exists: addon qty = parent qty.
   *   - If parent missing (token only on addons): addon qty = 0 (remove).
   * Returns { [item.key]: targetQuantity } for items needing change.
   */
  function computeUpdates(cart) {
    const items = cart.items || [];

    // Index parents by bundle token.
    const parentByToken = new Map();
    for (const item of items) {
      const props = item.properties || {};
      const token = props._bundle_id;
      if (token) parentByToken.set(token, item);
    }

    const updates = {};

    for (const item of items) {
      const props = item.properties || {};
      const parentToken = props._bundle_parent;
      if (!parentToken) continue;

      const parent = parentByToken.get(parentToken);

      if (!parent) {
        // Orphan — remove.
        if (item.quantity > 0) updates[item.key] = 0;
        continue;
      }

      if (item.quantity !== parent.quantity) {
        updates[item.key] = parent.quantity;
      }
    }

    return updates;
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  function collectCartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.getAttribute('data-section-id');
      if (id) ids.push(id);
    });
    return ids.join(',');
  }

  document.addEventListener('cart:update', (event) => {
    const detail = event.detail || {};
    const data = detail.data || {};
    const source = data.source;

    // Ignore our own update broadcasts to avoid feedback loops.
    if (source === 'jersey-bundle-cleanup') return;

    syncBundles(source || 'cart:update');
  });

  // Also run once on initial load in case the cart already has orphans from a previous session.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncBundles('initial-load'));
  } else {
    setTimeout(() => syncBundles('initial-load'), 100);
  }
})();
